// VersionHistoryPanel — the revision list beside the viewer: who saved what,
// when, the client-side +N/−N for that revision, and the two actions history
// exists for (Compare, Restore).
//
// Restore is copy-forward on the proxy: it appends a new revision holding the
// old content, so provenance stays truthful and nothing is ever rewound. The
// +N/−N counts are computed here because the list endpoint returns metadata
// only; each row lazily fetches its own content and its predecessor's, and RTK
// caches per revision id, so a row and the row below it share one request
// rather than doubling them.

import { Download, GitCompareArrows, Loader2, RotateCcw, X } from 'lucide-react'
import {
  useGetDocRevisionQuery,
  type DocRevisionMeta
} from '@axilon-apps/domains-ontology-ui-state'

import { Button } from '../../components/ui/button'
import { cn } from '../../lib/utils'
import { formatBytes } from './doc-helpers'
import { relativeTime, revisionLineDelta } from './doc-helpers'

export interface VersionHistoryPanelProps {
  docId: string
  revisions: readonly DocRevisionMeta[]
  isLoading: boolean
  hasMore: boolean
  /** Revision id whose restore is in flight. */
  restoringId: string | null
  /** Disabled while the doc is open in the editor (the lease is taken). */
  canRestore: boolean
  /** Markdown-only: line deltas and Compare. Binaries keep Restore (+ optional download). */
  showDiff?: boolean
  onCompare?: (revision: DocRevisionMeta) => void
  onRestore: (revision: DocRevisionMeta) => void
  onDownload?: (revision: DocRevisionMeta) => void
  onClose: () => void
}

const RevisionDelta = ({
  docId,
  revisionId,
  previousRevisionId
}: {
  docId: string
  revisionId: string
  previousRevisionId: string | null
}) => {
  const current = useGetDocRevisionQuery({ docId, revisionId })
  const previous = useGetDocRevisionQuery(
    { docId, revisionId: previousRevisionId ?? '' },
    { skip: previousRevisionId === null }
  )

  if (current.data === undefined) return null
  // The oldest revision on hand has nothing to diff against, so every line in
  // it counts as added — which is what "initial draft: +96 −0" should read as.
  const before = previousRevisionId === null ? '' : previous.data?.content
  if (before === undefined) return null

  const { added, removed } = revisionLineDelta(before, current.data.content)
  return (
    <span className="flex items-center gap-1.5 font-mono text-11">
      <span className="text-st-run">+{added}</span>
      <span className="text-st-down">−{removed}</span>
    </span>
  )
}

const VersionHistoryPanel = ({
  docId,
  revisions,
  isLoading,
  hasMore,
  restoringId,
  canRestore,
  showDiff = true,
  onCompare,
  onRestore,
  onDownload,
  onClose
}: VersionHistoryPanelProps) => (
  <aside
    className="flex h-full min-h-0 w-75 shrink-0 flex-col overflow-hidden border-l border-line bg-bg-elev"
    aria-label="Version history"
  >
    <header className="flex items-center gap-2 border-b border-line px-3.5 py-2.5">
      <h2 className="m-0 flex-1 text-13 font-semibold text-ink">Version history</h2>
      <Button size="icon-sm" variant="ghost" aria-label="Close version history" onClick={onClose}>
        <X aria-hidden />
      </Button>
    </header>
    <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-auto px-3.5 py-3">
      {isLoading ? (
        <span className="flex items-center gap-2 text-12 text-ink-3">
          <Loader2 className="size-3.5 animate-spin" aria-hidden />
          Loading revisions…
        </span>
      ) : revisions.length === 0 ? (
        <p className="m-0 text-12 text-ink-3">
          No revisions yet. The first save — yours or the agent&rsquo;s — starts the history.
        </p>
      ) : (
        revisions.map((revision, index) => {
          const previousRevisionId = revisions[index + 1]?.id ?? null
          const restoring = restoringId === revision.id
          return (
            <div key={revision.id} className="flex flex-col gap-1">
              <div className="flex items-baseline gap-2">
                <span
                  className={cn(
                    'mt-1 size-1.5 shrink-0 rounded-full',
                    revision.author_kind === 'agent' ? 'bg-brand' : 'bg-ink-3'
                  )}
                  aria-hidden
                />
                <span className="min-w-0 flex-1 truncate text-12 font-medium text-ink">
                  {revision.author_kind === 'agent'
                    ? `Agent · ${revision.author_name}`
                    : revision.author_name}
                </span>
                <span className="shrink-0 text-11 text-ink-3">
                  {relativeTime(revision.created_at)}
                </span>
              </div>
              {revision.summary !== '' ? (
                <p className="m-0 pl-3.5 text-12 leading-snug text-ink-2">{revision.summary}</p>
              ) : null}
              <div className="flex items-center gap-2 pl-3.5">
                {showDiff ? (
                  <RevisionDelta
                    docId={docId}
                    revisionId={revision.id}
                    previousRevisionId={previousRevisionId}
                  />
                ) : revision.size_bytes !== undefined && revision.size_bytes > 0 ? (
                  <span className="font-mono text-11 text-ink-3">
                    {revision.format ? `${revision.format.toUpperCase()} · ` : ''}
                    {formatBytes(revision.size_bytes)}
                  </span>
                ) : null}
                <div className="flex-1" />
                {onDownload ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 px-1.5 text-11"
                    onClick={() => onDownload(revision)}
                  >
                    <Download aria-hidden />
                    Download
                  </Button>
                ) : null}
                {showDiff && onCompare ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 px-1.5 text-11"
                    onClick={() => onCompare(revision)}
                  >
                    <GitCompareArrows aria-hidden />
                    Compare
                  </Button>
                ) : null}
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-6 px-1.5 text-11"
                  disabled={!canRestore || restoring}
                  title={
                    canRestore ? undefined : 'Close the editor to restore an earlier revision.'
                  }
                  onClick={() => onRestore(revision)}
                >
                  {restoring ? (
                    <Loader2 className="animate-spin" aria-hidden />
                  ) : (
                    <RotateCcw aria-hidden />
                  )}
                  Restore
                </Button>
              </div>
            </div>
          )
        })
      )}
      {hasMore ? (
        <p className="m-0 text-11 text-ink-3">
          Older revisions exist — they are kept forever, but only the most recent are listed here.
        </p>
      ) : null}
    </div>
  </aside>
)

export default VersionHistoryPanel
